/* eslint-disable max-lines */
import { Divider, Tab, Tabs, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { jobTypeOptions } from "@src/appV2/redesign/PlacementCandidate/constants";
import { JobType, type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { augmentYearsOfExperienceWithWorkerLicenses } from "@src/appV2/redesign/PlacementCandidate/utils";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useEffect, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";

import { type PlacementForComputedPay } from "../../lib/usePayTransparencyRates";
import { type TransparencyPayRate } from "../../types/payRate";
import {
  getLowestMinYearsOfExperience,
  getOfferedJobTypes,
  isJobTypeOffered,
} from "../../utils/pay-transparency";
import { usePayAndScheduleForm } from "../PayAndScheduleForm/usePayAndScheduleForm";
import { YearsOfExperienceInputChip } from "../PayAndScheduleForm/YearsOfExperienceInputChip";
import { BenefitsDiffCardV2 } from "./BenefitsDiffCardV2";
import { NoYearsOfExperiencePayDataAlert } from "./NoYearsOfExperiencePayDataAlert";
import { PayTableV2 } from "./PayTableV2";

export const PAY_AND_SCHEDULE_FORM_MAX_YEARS_OF_EXPERIENCE = 25;

interface PayAndScheduleFormProps {
  placement: PlacementForComputedPay;
  placementCandidate: PlacementCandidate;
  workerId: string;
  transparencyPayRates: TransparencyPayRate[];
  highestMatchingQualification: string;
  isYearsOfExperienceEditable?: boolean;
}

export function PayAndScheduleFormV2({
  transparencyPayRates,
  highestMatchingQualification,
  placement,
  placementCandidate,
  workerId,
  isYearsOfExperienceEditable = true,
}: PayAndScheduleFormProps) {
  const {
    useFormReturn: usePayAndScheduleFormReturn,
    updatePlacementCandidateFormData,
    isUpdating,
  } = usePayAndScheduleForm({
    placementDetails: placement,
    placementCandidate,
  });
  const { control, watch, setValue } = usePayAndScheduleFormReturn;

  const worker = useDefinedWorker();

  const [showNoYearsOfExperiencePayDataAlert, setShowNoYearsOfExperiencePayDataAlert] =
    useState(false);

  const selectedJobType = watch("jobType");
  const useCombinedBenefit = watch("useCombinedBenefit");
  const useHealthInsuranceBenefit = watch("useHealthInsuranceBenefit");
  const usePaidTimeOffBenefit = watch("usePaidTimeOffBenefit");
  const enteredYearsOfExperienceByQualification = watch("yearsOfExperienceByQualification");

  const yearsOfExperienceByQualification = augmentYearsOfExperienceWithWorkerLicenses(
    enteredYearsOfExperienceByQualification,
    worker.licenses
  );

  const hasBenefitsDiff = transparencyPayRates.some(
    (payRate) =>
      isDefined(selectedJobType) &&
      payRate.jobTypes.includes(selectedJobType) &&
      (isDefined(payRate.benefitsDiff) ||
        isDefined(payRate.healthBenefitsDiff) ||
        isDefined(payRate.ptoBenefitsDiff))
  );
  const showBenefitsToggle = hasBenefitsDiff || selectedJobType !== JobType.PER_DIEM;

  const hasSplitBenefitsDiff =
    selectedJobType !== JobType.PER_DIEM &&
    (transparencyPayRates.some(
      (payRate) =>
        isDefined(selectedJobType) &&
        payRate.jobTypes.includes(selectedJobType) &&
        !isDefined(payRate.benefitsDiff) &&
        (isDefined(payRate.healthBenefitsDiff) || isDefined(payRate.ptoBenefitsDiff))
    ) ??
      false);

  const isJobTypeOfferedHelper = useCallback(
    (jobType: JobType) => {
      return isJobTypeOffered({
        jobType,
        placement: { jobShifts: placement.jobShifts },
        transparencyPayRates,
      });
    },
    [placement?.jobShifts, transparencyPayRates]
  );

  const availableJobTypes = jobTypeOptions
    .filter((jobTypeOption) => jobTypeOption.value !== JobType.WEEKEND_WARRIOR)
    .filter((jobTypeOption) => isJobTypeOfferedHelper(jobTypeOption.value));

  useEffect(() => {
    if (
      isDefined(selectedJobType) &&
      !availableJobTypes.some((jobTypeOption) => jobTypeOption.value === selectedJobType)
    ) {
      setValue("jobType", availableJobTypes[0].value);
    }
  }, [selectedJobType, availableJobTypes, setValue]);

  useEffect(() => {
    if (!isDefined(selectedJobType) || transparencyPayRates.length === 0) {
      return;
    }

    const lowestMinYearsOfExperience = getLowestMinYearsOfExperience(
      transparencyPayRates,
      selectedJobType
    );

    const yearsOfExperience = yearsOfExperienceByQualification[highestMatchingQualification];
    if (
      isDefined(yearsOfExperience) &&
      lowestMinYearsOfExperience !== Number.POSITIVE_INFINITY &&
      yearsOfExperience < lowestMinYearsOfExperience
    ) {
      setValue("yearsOfExperienceByQualification", {
        ...yearsOfExperienceByQualification,
        [highestMatchingQualification]: lowestMinYearsOfExperience,
      });
      setShowNoYearsOfExperiencePayDataAlert(true);
    }
  }, [
    yearsOfExperienceByQualification,
    highestMatchingQualification,
    setValue,
    selectedJobType,
    transparencyPayRates,
  ]);

  const benefitsDifferential = {
    combined: useCombinedBenefit ?? true,
    health: useHealthInsuranceBenefit ?? true,
    pto: usePaidTimeOffBenefit ?? true,
  };

  const handleJobTypeChange = useCallback(
    (jobType: JobType) => {
      const newLowestMinYearsOfExperience = getLowestMinYearsOfExperience(
        transparencyPayRates,
        jobType
      );
      if (
        isDefined(yearsOfExperienceByQualification[highestMatchingQualification]) &&
        yearsOfExperienceByQualification[highestMatchingQualification] <
          newLowestMinYearsOfExperience
      ) {
        setShowNoYearsOfExperiencePayDataAlert(true);
        setValue("yearsOfExperienceByQualification", {
          ...yearsOfExperienceByQualification,
          [highestMatchingQualification]: newLowestMinYearsOfExperience,
        });
      } else {
        setShowNoYearsOfExperiencePayDataAlert(false);
      }

      updatePlacementCandidateFormData({ jobType });
    },
    [
      transparencyPayRates,
      yearsOfExperienceByQualification,
      highestMatchingQualification,
      setValue,
      updatePlacementCandidateFormData,
    ]
  );

  const logActionEvent = (params: {
    action: string;
    previousValue?: unknown;
    newValue?: unknown;
  }) => {
    const { action, previousValue, newValue } = params;
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_PAY_AND_SCHEDULE_FORM_ACTIONS, {
      action,
      placementId: placement.id,
      placementCandidateId: placementCandidate.id,
      workerId,
      workplaceId: placement.workplaceId,
      previousValue,
      newValue,
      highestMatchingQualification,
      offeredJobTypes: getOfferedJobTypes({
        placement: { jobShifts: placement.jobShifts },
        transparencyPayRates,
      }),
    });
  };

  if (!isDefined(selectedJobType)) {
    return null;
  }

  return (
    <FormProvider {...usePayAndScheduleFormReturn}>
      <Stack spacing={5}>
        {isJobTypeOfferedHelper(selectedJobType) && showNoYearsOfExperiencePayDataAlert && (
          <NoYearsOfExperiencePayDataAlert
            jobType={selectedJobType}
            lowestMinYearsOfExperience={getLowestMinYearsOfExperience(
              transparencyPayRates,
              selectedJobType
            )}
          />
        )}
        <Stack spacing={4}>
          <Controller
            name="jobType"
            control={control}
            render={({ field }) => (
              <Tabs
                value={field.value ?? ""}
                variant="pill"
                onChange={(_, newValue) => {
                  const newJobType = newValue as JobType;
                  const isOffered = isJobTypeOfferedHelper(newJobType);

                  if (isOffered) {
                    logActionEvent({
                      action: "job_type_selected",
                      previousValue: field.value,
                      newValue: newJobType,
                    });

                    handleJobTypeChange(newJobType);
                    field.onChange(newJobType);
                  }
                }}
              >
                {availableJobTypes.map((jobTypeOption) => {
                  const isOffered = isJobTypeOfferedHelper(jobTypeOption.value);
                  return (
                    <Tab
                      key={jobTypeOption.value}
                      variant="pill"
                      label={jobTypeOption.label}
                      value={jobTypeOption.value}
                      disabled={!isOffered}
                      aria-label={`${jobTypeOption.label} job type tab`}
                    />
                  );
                })}
              </Tabs>
            )}
          />
          {isJobTypeOfferedHelper(selectedJobType) && (
            <PayTableV2
              placement={placement}
              transparencyPayRates={transparencyPayRates}
              highestMatchingQualification={highestMatchingQualification}
              selectedJobType={selectedJobType}
              benefitsDifferential={benefitsDifferential}
              placementCandidateId={placementCandidate.id}
              workerId={workerId}
            />
          )}
          <Stack spacing={5} sx={{ pt: 3, px: 5 }}>
            {isYearsOfExperienceEditable && (
              <Controller
                name="yearsOfExperienceByQualification"
                control={control}
                render={({ field }) => (
                  <Stack
                    spacing={2}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack spacing={1}>
                      <Text semibold variant="body2">
                        Years of experience
                      </Text>
                      <Text
                        variant="caption"
                        color={(theme) => theme.palette.text.tertiary}
                        sx={{ maxWidth: 210 }}
                      >
                        {isDefined(field.value)
                          ? "This rate is being generated based on your years of experience"
                          : "Add your years of experience to get a more accurate rate"}
                      </Text>
                    </Stack>
                    <YearsOfExperienceInputChip
                      transparencyPayRates={transparencyPayRates}
                      selectedJobType={selectedJobType}
                      baseLabel="Select"
                      value={yearsOfExperienceByQualification[highestMatchingQualification]}
                      max={PAY_AND_SCHEDULE_FORM_MAX_YEARS_OF_EXPERIENCE}
                      onMenuOpen={() => {
                        logActionEvent({ action: "years_of_experience_opened" });
                      }}
                      onChange={(value) => {
                        if (isDefined(value) && !Number.isNaN(value)) {
                          logActionEvent({
                            action: "years_of_experience_selected",
                            previousValue: field.value,
                            newValue: value,
                          });

                          const newValue = {
                            ...field.value,
                            [highestMatchingQualification]: value,
                          };

                          field.onChange(newValue);
                          updatePlacementCandidateFormData({
                            yearsOfExperienceByQualification: newValue,
                          });

                          setShowNoYearsOfExperiencePayDataAlert(false);
                        }
                      }}
                    />
                  </Stack>
                )}
              />
            )}

            {showBenefitsToggle && (
              <>
                <Divider color="primary" />
                <BenefitsDiffCardV2
                  transparencyPayRates={transparencyPayRates}
                  hasSplitBenefitsDiff={hasSplitBenefitsDiff}
                  selectedJobType={selectedJobType}
                  placement={placement}
                  placementCandidateId={placementCandidate.id}
                  workerId={workerId}
                  highestMatchingQualification={highestMatchingQualification}
                  isUpdating={isUpdating}
                  onBenefitsChange={updatePlacementCandidateFormData}
                />
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
/* eslint-enable max-lines */
